import { RefObject, useCallback, useLayoutEffect, useState } from 'react'

import { DropdownPosition, SurfacePlacement } from './types'

const GAP = 0
const VIEWPORT_MARGIN = 8

type Props = {
  opened: boolean
  anchorRef: RefObject<HTMLElement | null>
  surfaceRef: RefObject<HTMLElement | null>
  mousePosition: RefObject<{
    x: number
    y: number
  }>
  placement: SurfacePlacement
}

const defaultPosition: DropdownPosition = {
  placement: 'bottom',
  style: {
    position: 'fixed',
    visibility: 'hidden',
  },
}

export const useDropdownPosition = ({
  opened,
  anchorRef,
  surfaceRef,
  mousePosition,
  placement,
}: Props) => {
  const [position, setPosition] = useState<DropdownPosition>(defaultPosition)

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current
    const surface = surfaceRef.current

    if (!anchor || !surface) {
      return
    }

    const anchorRect = anchor.getBoundingClientRect()

    const surfaceHeight = surface.offsetHeight
    const surfaceWidth = surface.offsetWidth

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const { x, y } = mousePosition.current || { x: 0, y: 0 }

    if (x && y && placement === SurfacePlacement.CursorTopRight) {
      const maxHeight = Math.max(100, y - VIEWPORT_MARGIN - GAP)

      let top = y - Math.min(surfaceHeight, maxHeight)
      let left = x

      if (left + surfaceWidth > viewportWidth - VIEWPORT_MARGIN) {
        left = viewportWidth - surfaceWidth - VIEWPORT_MARGIN
      }

      left = Math.max(VIEWPORT_MARGIN, left)
      top = Math.max(VIEWPORT_MARGIN, top)

      setPosition((prev) => {
        if (
          prev.placement === 'top' &&
          prev.style.top === top &&
          prev.style.left === left &&
          prev.style.maxHeight === maxHeight
        ) {
          return prev
        }

        return {
          placement: 'top',
          style: {
            position: 'fixed',
            top,
            left,
            maxHeight,
            overflowY: 'auto',
          },
        }
      })

      return
    }

    const spaceTop = anchorRect.top - VIEWPORT_MARGIN

    const spaceBottom = viewportHeight - anchorRect.bottom - VIEWPORT_MARGIN

    const openBottom = spaceBottom >= surfaceHeight || spaceBottom >= spaceTop

    const maxHeight = Math.max(100, openBottom ? spaceBottom - GAP : spaceTop - GAP)

    let top = openBottom
      ? anchorRect.bottom + GAP
      : anchorRect.top - GAP - Math.min(surfaceHeight, maxHeight)

    let left = anchorRect.left

    if (left + surfaceWidth > viewportWidth - VIEWPORT_MARGIN) {
      left = viewportWidth - surfaceWidth - VIEWPORT_MARGIN
    }

    left = Math.max(VIEWPORT_MARGIN, left)

    setPosition((prev) => {
      const placement = openBottom ? 'bottom' : 'top'

      if (
        prev.placement === placement &&
        prev.style.top === top &&
        prev.style.left === left &&
        prev.style.minWidth === anchorRect.width &&
        prev.style.maxHeight === maxHeight
      ) {
        return prev
      }

      return {
        placement,
        style: {
          position: 'fixed',
          top,
          left,
          minWidth: anchorRect.width,
          maxHeight,
          overflowY: 'auto',
        },
      }
    })
  }, [anchorRef, surfaceRef, mousePosition, placement])

  useLayoutEffect(() => {
    if (!opened) {
      return
    }

    updatePosition()
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updatePosition)
    })

    if (anchorRef.current) {
      resizeObserver.observe(anchorRef.current)
    }

    if (surfaceRef.current) {
      resizeObserver.observe(surfaceRef.current)
    }

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [opened, updatePosition, surfaceRef, anchorRef])

  return { position, updatePosition }
}
